/*
 * Billing-vakiot (Sessio 8b).
 *
 * Yksi totuus plan-budjeteille, Stripe-price-ID:lle, plan-hinnoille ja
 * plan-otsikoille. Sekaa worker (budjettitarkistus + price-ID-allowlist) etta
 * app (BillingPage + TrialBanner) lukee tasta tiedostosta.
 *
 * Lahde: SEED v1.14 luku 3.2 (Hinnoittelumalli) + 16.7 (Token-kayttodashboard) +
 * Sessio 8a:n lib/ai/budget.ts:n PLAN_BUDGETS.
 *
 * Konventio: vakiot ovat `as const` -literaaleja, jotta tyypit pysyvat tarkkoina.
 * Plan-tyyppi tulee @shared/types/db:sta jotta DB-skeema ja vakiot pysyvat synkassa.
 */

import type { Plan } from '../types/db';

// =============================================================================
// Trial-jakso
// =============================================================================

/** Trial-jakson pituus paivina. Asetetaan trial_ends_at = now + TRIAL_PERIOD_DAYS uusille orgille. */
export const TRIAL_PERIOD_DAYS = 14;

// =============================================================================
// Plan-budjetit (token-kulutuksen kuukausikatto)
// =============================================================================

/**
 * Plan-kohtaiset kuukausibudjetit (input + output tokens).
 *
 * Luvut perustuvat Anthropic-hinnoittelun tasapaino-arvioon:
 * - Trial: ~5 EUR / kk -> 200k input / 100k output
 * - Solo: ~15 EUR / kk -> 600k / 300k
 * - Team: ~50 EUR / kk -> 2M / 1M
 * - Business: ~200 EUR / kk -> 8M / 4M
 *
 * Tarkennetaan Sessio 9:ssa kun saadaan ensimmaisia kayton mittareita.
 * Sessio 8:ssa luvut ovat kovakoodattuja. v1.1:ssa siirtyy
 * organizations.features-JSON:ksi tai erilliseen plan_configurations-tauluun.
 */
export const PLAN_BUDGETS: Record<Plan, { input: number; output: number }> = {
  trial: { input: 200_000, output: 100_000 },
  solo: { input: 600_000, output: 300_000 },
  team: { input: 2_000_000, output: 1_000_000 },
  business: { input: 8_000_000, output: 4_000_000 },
};

/**
 * Plan-kohtainen mediakiintio tavuina (K158, audit 10; Petrin paatos 2.8.2026).
 *
 * Video nosti per-tiedostorajan 5 MB -> 90 MB eika org-kohtaista kattoa ollut lainkaan.
 * Raja on VAARINKAYTON ESTO, ei kustannusoptimointi: R2 maksaa ~0,015 $/GB/kk, joten
 * Business-taso on ~3 $/kk. Solo 10 GB = ~110 videota tai tuhansia kuvia -- normaali
 * kayttaja ei osu rajaan. Nostettavissa muuttamalla tata vakiota (ei migraatiota).
 *
 * Trial saa saman kuin solo: kokeilijan pitaa paasta testaamaan videota oikeasti.
 */
export const PLAN_STORAGE_BYTES: Record<Plan, number> = {
  trial: 10 * 1024 * 1024 * 1024, // 10 GB
  solo: 10 * 1024 * 1024 * 1024, // 10 GB
  team: 50 * 1024 * 1024 * 1024, // 50 GB
  business: 200 * 1024 * 1024 * 1024, // 200 GB
};

/**
 * Plan-kohtainen uutiskirjelistan kontaktiraja (Petrin paatos 2.8.2026, viimeinen Tier 1
 * -hinnoittelupaatos; Joannan kysymys 11.7.).
 *
 * PEHMEA KATTO: rajan tayttyessa uusia tilaajia ei voi lisata, mutta olemassa olevat sailyvat
 * ja lahetys jatkuu normaalisti -- kukaan ei menyta listaansa eika kesken olevaa kampanjaa.
 * Ylimenevasta EI veloiteta erikseen: mitattu laskutus (Stripe metered) vaatisi usage-raportoinnin
 * ja proraatiokasittelyn, ja plan-paivitys tuottaa saman lopputuloksen yksinkertaisemmin.
 *
 * Sama periaate kuin PLAN_STORAGE_BYTES: raja on vaarinkayton ja lahetyskustannuksen esto, ei
 * myyntikeino. Pk-yrityksen lista on tyypillisesti satoja, ei tuhansia. Nostettavissa tata
 * vakiota muuttamalla (ei migraatiota). Trial saa saman kuin solo.
 *
 * Aiemmin katto oli KIINTEA 50 000 / org (MAX_SUBSCRIBERS_PER_ORG) ja koski vain sisaista
 * lisaysta + CSV-tuontia -- julkinen double-opt-in-lomake ohitti sen kokonaan.
 */
export const PLAN_CONTACT_LIMITS: Record<Plan, number> = {
  trial: 500,
  solo: 500,
  team: 2_000,
  business: 10_000,
};

/** Plan-tason kontaktiraja; tuntematon/puuttuva plan -> trialin raja (turvallinen suunta). */
export function getContactLimit(plan: string | null | undefined): number {
  return PLAN_CONTACT_LIMITS[(plan ?? 'trial') as Plan] ?? PLAN_CONTACT_LIMITS.trial;
}

// =============================================================================
// Plan-kohtaiset jasenmaararajat (Sessio 13a)
// =============================================================================

/**
 * Maksimi-kayttajamaara per organisaatio plan-tasolla.
 *
 * Trial + Solo ovat yhden hengen tilauksia (lukittu Sessio 13:n suunnittelussa).
 * Team antaa 5 jasenta, Business 25. Numero tarkoittaa accepted-tilan kayttajia
 * + avoimia kutsuja yhteensa -- estamme overcommit:in jo invite-luontihetkella.
 *
 * organizations.max_users-sarake on default 1 (init-migraatio); Stripe-webhook
 * Sessio 19:ssa paivittaa rivin kun plan vaihtuu. Sessio 13:ssa team.service
 * lukee getMaxUsers(plan):sta jotta on yksi totuus -- DB-arvon kanssa ei tehda
 * disagreement-tarkistuksia. Vaihe 2:n alussa siirrytaan plan_configurations-
 * tauluun tai organizations.features-JSON:iin.
 */
export const PLAN_LIMITS: Record<Plan, { maxUsers: number; maxActivePlans: number; maxFolders: number }> = {
  trial: { maxUsers: 1, maxActivePlans: 1, maxFolders: 5 },
  solo: { maxUsers: 1, maxActivePlans: 1, maxFolders: 5 },
  team: { maxUsers: 5, maxActivePlans: 3, maxFolders: 25 },
  business: { maxUsers: 25, maxActivePlans: 10, maxFolders: 100 },
};

/** Palauttaa plan-tason maksimi-kayttajamaaran. */
export function getMaxUsers(plan: Plan): number {
  return PLAN_LIMITS[plan].maxUsers;
}

/**
 * Strategiasuunnitelmat (planning-suite OSA C / C1, strategy-tool.md § 7): ei-arkistoitujen
 * suunnitelmien enimmaismaara per org. Tuntematon plan -> trial-raja (sama puolustus kuin getContactLimit).
 */
export function getMaxActivePlans(plan: string | null | undefined): number {
  return (PLAN_LIMITS[(plan ?? 'trial') as Plan] ?? PLAN_LIMITS.trial).maxActivePlans;
}

/**
 * Kansiot Ideoissa (TYÖJONO 28, docs/design/folders.md, Petri 8.10.2026): Solo 5, Team 25, Business 100.
 * Tason laskiessa olemassa olevat kansiot säilyvät, uusia ei voi luoda (sama kuin strategioilla).
 */
export function getMaxFolders(plan: string | null | undefined): number {
  return (PLAN_LIMITS[(plan ?? 'trial') as Plan] ?? PLAN_LIMITS.trial).maxFolders;
}

// =============================================================================
// Stripe Price ID:t (LIVE, Innovasystems-tili 15.8.2026)
// =============================================================================

/**
 * Stripe Price ID:t -- LIVE-tilan ID:ita (Innovasystems-tili) 15.8.2026 alkaen.
 * Nimi oli 16.8.2026 asti STRIPE_PRICE_IDS_SANDBOX, mika oli kytkennan jalkeen
 * suoraan valheellinen: nimi lupasi testitilan arvot mutta naista veloitetaan oikeaa
 * rahaa. Vaara nimi on vaarallisempi kuin ei nimea lainkaan -- se ohjaa kokeilemaan.
 *
 * Frontend lukee tasta Checkout-pyyntoon. Backend validoi env-muuttujia vasten
 * (allowedPriceIds worker/src/routes/billing.ts:ssa) -- jos arvot eivat ole synkassa,
 * pyynto saa INVALID_PRICE_ID-virheen. Siksi tama vakio ja wrangler.tomlin MOLEMMAT
 * env-lohkot paivitetaan aina samassa commitissa.
 */
export const STRIPE_PRICE_IDS = {
  // Hinnoittelu-uudistus 6.9.2026 (Petri): 39/119/199 EUR/kk. Lookup keyt solo_monthly jne.
  // Vanhat 49/149/249-hinnat (price_1U4eZn.../price_1U4ebj.../price_1U4eca...) ovat webhookin
  // legacy-mappauksessa (wrangler.toml STRIPE_PRICE_*_MONTHLY_LEGACY), eivat allowlistissa.
  solo: 'price_1UChlPCY3awev9Vm2hKwpOnu',
  team: 'price_1UChpVCY3awev9Vmw2QvHxAC',
  business: 'price_1UChqsCY3awev9Vml9abrAyk',
} as const;

export type PaidPlan = 'solo' | 'team' | 'business';

// =============================================================================
// Plan-otsikot ja kuvaukset (i18n-helpper)
// =============================================================================

export const PLAN_LABELS_FI: Record<Plan, string> = {
  trial: 'Kokeilujakso',
  solo: 'Solo',
  team: 'Team',
  business: 'Business',
};

export const PLAN_LABELS_EN: Record<Plan, string> = {
  trial: 'Trial',
  solo: 'Solo',
  team: 'Team',
  business: 'Business',
};

// =============================================================================
// Plan-hinnat (EUR/kk)
// =============================================================================

/**
 * Tilauksen kuukausihinta euroina -- UI-NAYTTOHINTA (BillingPage, landing).
 *
 * !! TARKEA: tama on VAIN naytto. Todellisen veloituksen maaraa Stripe-tuotteen
 * hinta STRIPE_PRICE_IDS-price-ID:n takana. Jos nama eroavat, kayttaja nakee eri
 * hinnan kuin hanelta veloitetaan -- ja 15.8.2026 alkaen veloitus on OIKEAA RAHAA.
 * Kun tata muuttaa, PETRIN ON paivitettava vastaavat Stripe-tuotehinnat live-tilan
 * Dashboardissa tasmaamaan (display-vakion muutos ei muuta mita Stripe veloittaa).
 *
 * Compliance-gate 10.7.2026: 79/249/499 -> 49/149/249 (Petrin uusi hinnoittelu,
 * SEED 3.2 -haarukan sisalla). Stripe-tuotteet paivitettava erikseen.
 */
export const PLAN_PRICES_EUR_MONTHLY: Record<PaidPlan, number> = {
  // Hinnoittelu-uudistus 6.9.2026 (Petri): 49/149/249 -> 39/119/199. Stripe-hinnat vaihdettu
  // samalla (uudet price-ID:t samoilla tuotteilla, vanhat arkistoidaan deployn jalkeen).
  solo: 39,
  team: 119,
  business: 199,
};

// =============================================================================
// Vuositilaus (Petrin ehdotus 2.9.2026, Joanna hyvaksyi 4.9.2026)
// =============================================================================

export type BillingInterval = 'monthly' | 'yearly';

/** Kaava: maksa 10 kuukautta, saat 12 -> "2 kuukautta veloituksetta" (-16,7 %). Vain viestintaan. */
export const YEARLY_FREE_MONTHS = 2;

/**
 * Vuosihinnat euroina, ALV 0 (sama linjaus kuin kuukausihinnoilla). 10 x kuukausihinta.
 * Kun tata muuttaa, Petrin on paivitettava Stripen vuosihinnat vastaamaan.
 */
export const PLAN_PRICES_EUR_YEARLY: Record<PaidPlan, number> = {
  solo: 390,
  team: 1190,
  business: 1990,
};

/**
 * Vuosihintojen Stripe-price-ID:t (live-tili, sama tuote kuin kuukausihinta, interval=year).
 * TYHJA merkkijono = hintaa ei ole viela luotu Stripeen -> `hasYearlyPrices()` on false ja
 * UI piilottaa vuosivalinnan kokonaan. Sama synkkasaanto kuin STRIPE_PRICE_IDS:lla:
 * wrangler.tomlin STRIPE_PRICE_*_YEARLY paivitetaan SAMASSA commitissa, muuten Checkout saa
 * INVALID_PRICE_ID:n.
 */
export const STRIPE_PRICE_IDS_YEARLY: Record<PaidPlan, string> = {
  // Luotu Stripeen 6.9.2026 (lookup keyt solo_yearly / team_yearly / business_yearly).
  solo: 'price_1UChlPCY3awev9VmrfSRn6sy',
  team: 'price_1UChpVCY3awev9VmmOAB1p6H',
  business: 'price_1UChqsCY3awev9VmMk7HqLxM',
};

/** Onko vuosilaskutus ostettavissa (kaikki kolme price-ID:ta taytetty). */
export function hasYearlyPrices(): boolean {
  return (['solo', 'team', 'business'] as const).every((p) => STRIPE_PRICE_IDS_YEARLY[p].length > 0);
}

/** Checkoutiin lahetettava price-ID valitulle planille ja laskutusvalille. */
export function priceIdFor(plan: PaidPlan, interval: BillingInterval): string {
  return interval === 'yearly' ? STRIPE_PRICE_IDS_YEARLY[plan] : STRIPE_PRICE_IDS[plan];
}

/**
 * Naytettavat hinnat ovat VEROTTOMIA -- ALV lisataan paalle (Petrin paatos 1.8.2026,
 * sama linjaus kuin nettisivun "(alv 0 %)"). Stripe-tuotteiden `tax_behavior` on
 * `exclusive`, ja Stripe Tax laskee todellisen prosentin asiakkaan sijainnin mukaan:
 * suomalainen asiakas 25,5 %, EU-B2B ALV-tunnuksella 0 % (reverse charge).
 *
 * Tama vakio on VAIN UI-merkintaa varten ("+ alv 25,5 %") -- laskennan tekee Stripe,
 * ei sovellus. Jos Suomen ALV-kanta muuttuu, tama paivitetaan mutta veloitus muuttuu
 * Stripen puolella joka tapauksessa oikein.
 */
export const VAT_RATE_FI_PERCENT = 25.5;

// =============================================================================
// Token-pack (lisatokenit, Sessio 19b)
// =============================================================================

/**
 * Universaali token-pakki (Sessio 19b). Kertaluontoinen top-up KULUVALLE
 * laskutuskaudelle -- ei pysyva budjetin nosto (billing_period asetetaan
 * ostohetkella, checkBudget suodattaa kuluvan kauden mukaan).
 *
 * Token-maarat koodataan tahan: webhook lukee ne token_budget_allowances-
 * INSERT:iin. priceEur on UI-naytto + vastaa Stripe-tuotteen hintaa
 * (price_id backend-env:ssa STRIPE_PRICE_TOKEN_PACK, ei clientille).
 *
 * Hinnoittelu (Petri, 19b): raaka Sonnet-kustannus +1M input / +500k output
 * ~9,66 EUR -> ~2x konvenienssipreemio. BYOK-tietoinen (B3: BYOK on v1.0:n
 * primaarimalli, joten pakin oltava kilpailukykyinen -- ei "baarikate").
 */
export const TOKEN_PACK = {
  input: 1_000_000,
  output: 500_000,
  priceEur: 19,
} as const;

// =============================================================================
// Plan-ominaisuusmatriisi (P3, plan-vertailu — Joannan palaute 11.7.2026)
// =============================================================================

/**
 * Vertailun rivit: kaikki ominaisuudet x 3 tasoa (Solo/Team/Business), jotta plania
 * valittaessa vertailu onnistuu (Joanna: "tarkka lista kaikista ominaisuuksista").
 *
 * `kind:'value'` -> solukko lasketaan komponentissa: users/tokens JOHDETAAN
 * PLAN_LIMITS/PLAN_BUDGETS:sta (ei driftia), projects/support i18n:sta. `kind:'bool'`
 * -> ✓ / –. Rivin otsikko: i18n `billing.matrix.row.<key>`. Tason-portit peilaavat
 * koodin requirePlan-gateja (SEED 12.16.5 Team+ analytiikka, SEED 112 vuosikello/teemapaivat,
 * 12.19.4 hyvaksynta, jne.). Ads/ROAS jatetty pois kunnes Vaihe 7 tuotannossa (Petrin paatos).
 *
 * Nettisivun hinnoittelu (staattinen site/) pidetaan TASSA jarjestyksessa manuaalisesti
 * synkassa -- se ei voi importoida TS-vakiota (ei build-steppia).
 */
export type PlanMatrixRow =
  | { key: string; kind: 'value' }
  | { key: string; kind: 'bool'; solo: boolean; team: boolean; business: boolean };

export const PLAN_MATRIX: readonly PlanMatrixRow[] = [
  { key: 'users', kind: 'value' },
  { key: 'tokens', kind: 'value' },
  { key: 'projects', kind: 'value' },
  { key: 'plans', kind: 'value' },
  // TYÖJONO 28 (Petri 8.10.2026): kansiot Ideoissa 5 / 25 / 100.
  { key: 'folders', kind: 'value' },
  // Joanna 14.9.2026 (PAR-H1): sivuston hintataulukko listaa kontaktit ja tallennustilan, app ei -> sama lähde.
  { key: 'contacts', kind: 'value' },
  { key: 'storage', kind: 'value' },
  { key: 'lifecycle', kind: 'bool', solo: true, team: true, business: true },
  { key: 'calendar', kind: 'bool', solo: true, team: true, business: true },
  { key: 'yearWheel', kind: 'bool', solo: false, team: true, business: true },
  { key: 'themeDays', kind: 'bool', solo: false, team: false, business: true },
  { key: 'email', kind: 'bool', solo: true, team: true, business: true },
  { key: 'senderName', kind: 'bool', solo: false, team: true, business: true },
  { key: 'aiVersions', kind: 'bool', solo: true, team: true, business: true },
  { key: 'analyticsBasic', kind: 'bool', solo: true, team: true, business: true },
  { key: 'ga4', kind: 'bool', solo: false, team: true, business: true },
  { key: 'reports', kind: 'bool', solo: false, team: true, business: true },
  { key: 'budget', kind: 'bool', solo: false, team: true, business: true },
  { key: 'approval', kind: 'bool', solo: false, team: true, business: true },
  { key: 'assistant', kind: 'bool', solo: false, team: true, business: true },
  { key: 'gdpr', kind: 'bool', solo: false, team: false, business: true },
  // 1.11.2026 (Petri): MFA on käytössä kaikilla tasoilla mutta ei pakotettu backendissä -> ei "pakollinen" Businessille.
  { key: 'mfa', kind: 'bool', solo: true, team: true, business: true },
  { key: 'support', kind: 'value' },
] as const;

/** Token-luvun lyhennys matriisin solukkoon: 600000 -> "600k", 2000000 -> "2M". */
export function formatTokenBudget(n: number): string {
  return n >= 1_000_000 ? `${n / 1_000_000}M` : `${n / 1000}k`;
}
